import type { FlowItem } from "../Types/BookShipmentFlowItemType";
import type { ReactNode } from "react";

// A done step is rendered *lighter* than a locked one — that ordering is
// deliberate in the original (#ADADAD done vs #858585 locked), so it maps to
// faint and muted respectively rather than collapsing to one grey.
function progressTextColorClass(
  isCurrent: boolean,
  isDone: boolean,
  isUnlocked: boolean,
): string {
  if (isCurrent) return "text-foreground";
  if (isDone) return "text-content-faint";
  if (isUnlocked) return "text-foreground";
  return "text-content-muted";
}

interface BookShipmentNavigationSectionProps {
  currentFlowIdentifier: string;
  flow: Array<FlowItem>;
  additionalContentProvider?:
    | ((flowIdentifier: string) => ReactNode)
    | undefined;
  completedFlowItems: Array<string>;
  unlockedFlowItems: Array<string>;

  onNavigate: (
    currentFlowItem: string,
    proposedFlowItem: string,
  ) => Promise<void>;
}

interface BookShipmentNavigationSectionIconProps {
  value: string;
  isDone: boolean;
  isCurrent: boolean;
  isUnlocked: boolean;
}

function BookShipmentNavigationSectionIcon_(
  props: BookShipmentNavigationSectionIconProps,
) {
  return (
    <div className="flex flex-col items-center">
      {props.isDone ? (
        <div
          style={{
            width: "28px",
            height: "28px",
            backgroundColor: "var(--color-brand-subtle)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
          >
            <g fill="none" fillRule="evenodd">
              <path d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z" />
              <path
                fill="var(--color-blue)"
                d="M21.546 5.111a1.5 1.5 0 0 1 0 2.121L10.303 18.475a1.6 1.6 0 0 1-2.263 0L2.454 12.89a1.5 1.5 0 1 1 2.121-2.121l4.596 4.596L19.424 5.111a1.5 1.5 0 0 1 2.122 0"
              />
            </g>
          </svg>
        </div>
      ) : (
        <div
          style={{
            width: "28px",
            height: "28px",
            backgroundColor: "var(--color-blue)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            border: `3px solid var(--color-brand-subtle)`,
          }}
        >
          {props.value}
        </div>
      )}
    </div>
  );
}

interface BookShipmentNavigationSectionRowProps {
  index: number;
  flowItem: FlowItem;
  additionalContent: ReactNode | undefined;
  isCurrent: boolean;
  isDone: boolean;
  isUnlocked: boolean;
  isLast: boolean;

  // preNavigationCheck: () => boolean;
  // onInvalidate: () => Promise<void>;
  // baseUrlComponent: string;
  // urlQuery: Map<string, string> | undefined;

  currentFlowIdentifier: string;
  onNavigate: (
    currentFlowItem: string,
    proposedFlowItem: string,
  ) => Promise<void>;
}

function BookShipmentNavigationSectionRow_(
  props: BookShipmentNavigationSectionRowProps,
) {
  const isNavigable = props.isUnlocked;

  async function navigate() {
    if (!isNavigable) {
      return;
    }

    props.onNavigate(props.currentFlowIdentifier, props.flowItem.identifier);
  }

  const getNavigateButtonText = () => {
    switch (props.flowItem.identifier) {
      case "details":
        return "change details";
      case "quotes":
        return "change quote";
      case "pickup-address":
        return "change address";
      case "delivery-address":
        return "change address";
      case "broker":
        return "change broker";
      case "dangerous-goods":
        return "change goods";
      case "payment":
        return "change payment";
      case "review":
        return "change review";
      default:
        return "change";
    }
  };

  const navigateButtonText = getNavigateButtonText();

  return (
    <div
      className="flex flex-row items-start"
      style={{
        marginBottom: "24px",
      }}
    >
      <div
        className="flex flex-col items-center"
        style={{
          flex: 1,
          alignSelf: "stretch",
        }}
      >
        <BookShipmentNavigationSectionIcon_
          isDone={props.isDone}
          isCurrent={props.isCurrent}
          isUnlocked={props.isUnlocked}
          value={props.index.toString()}
        />
        <div className="h-[24px]" />
        {!props.isLast && (
          <div
            style={{
              width: "2px",
              height: "100%",
              backgroundColor: "var(--color-very-light-blue)",
            }}
          ></div>
        )}
      </div>
      <div className="w-[16px] shrink-0" />
      <div
        className={`font-semibold ${props.isCurrent ? "text-xl" : "text-base"} ${progressTextColorClass(
          props.isCurrent,
          props.isDone,
          props.isUnlocked,
        )} ${/* This is used by the E2E tests to identify which stage is locked */ ""}${
          props.isUnlocked ? "unlocked" : "locked"
        }`}
      >
        <div className="flex flex-col items-start">
          {props.flowItem.description}
          {/* The original set a quoted `color: "#696969"`, which is invalid
              CSS and never applied — so this inherits, as it always did. */}
          <div className="text-xs font-normal">{props.additionalContent}</div>
          {props.isDone && (
            <button
              type="button"
              className="mt-2 cursor-pointer rounded-full border-none bg-brand-subtle px-4 py-[5px] text-xs font-normal text-brand-text hover:brightness-[0.98]"
              onClick={navigate}
            >
              {navigateButtonText}
            </button>
          )}
        </div>
      </div>
      {props.flowItem.adournment && (
        <div
          className={`ml-2 font-semibold ${props.isCurrent ? "text-2xl" : "text-base"}`}
        >
          {props.flowItem.adournment}
        </div>
      )}
    </div>
  );
}

function BookShipmentNavigationSection(
  props: BookShipmentNavigationSectionProps,
) {
  return (
    <div className="flex flex-col items-start">
      {props.flow.map((flowItem, index) => {
        return (
          <BookShipmentNavigationSectionRow_
            key={flowItem.identifier}
            index={index + 1}
            flowItem={flowItem}
            additionalContent={props.additionalContentProvider?.(
              flowItem.identifier,
            )}
            isCurrent={flowItem.identifier === props.currentFlowIdentifier}
            isDone={props.completedFlowItems.includes(flowItem.identifier)}
            isUnlocked={props.unlockedFlowItems.includes(flowItem.identifier)}
            isLast={index === props.flow.length - 1}
            onNavigate={props.onNavigate}
            currentFlowIdentifier={props.currentFlowIdentifier}
          />
        );
      })}
    </div>
  );
}
export default BookShipmentNavigationSection;
